import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";
import { FiBook, FiBarChart2, FiUploadCloud, FiSearch } from "react-icons/fi";
import { MdOutlineDelete, MdEdit } from "react-icons/md";
import { baseApiURL } from "../../baseUrl";

const initialForm = {
  bookName: "",
  bookCode: "",
  author: "",
  genre: "",
  quantity: "",
  rackNumber: "",
  publisher: "",
  publishedYear: "",
  notes: "",
};

const AddBook = () => {
  const [formData, setFormData] = useState(initialForm);
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [selectedTab, setSelectedTab] = useState("add");
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBooks();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredBooks(books);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredBooks(
      books.filter(
        (book) =>
          book.bookName.toLowerCase().includes(term) ||
          book.author.toLowerCase().includes(term) ||
          String(book.bookCode).includes(term)
      )
    );
  }, [books, searchTerm]);

  const stats = useMemo(() => {
    const totalCopies = books.reduce((acc, item) => acc + (item.quantity || 0), 0);
    const totalIssued = books.reduce((acc, item) => acc + (item.issuedCount || 0), 0);
    const available = Math.max(totalCopies - totalIssued, 0);
    const lowStock = books.filter(
      (item) => item.quantity - (item.issuedCount || 0) <= 2
    );
    const topBorrowed = [...books]
      .sort((a, b) => (b.issuedCount || 0) - (a.issuedCount || 0))
      .slice(0, 5);
    return { totalCopies, totalIssued, available, lowStock, topBorrowed };
  }, [books]);

  const fetchBooks = () => {
    axios
      .get(`${baseApiURL()}/library/getLibraryBook`)
      .then((response) => {
        if (response.data.success) {
          setBooks(response.data.book || []);
          setFilteredBooks(response.data.book || []);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => toast.error(error.response?.data?.message || error.message));
  };

  const handleSubmit = (payload, isUpdate = false) => {
    if (!payload.bookName || !payload.bookCode || !payload.author || !payload.quantity) {
      toast.error("Please fill all mandatory fields");
      return;
    }

    toast.loading(isUpdate ? "Updating book..." : "Adding book...");
    const request = isUpdate
      ? axios.put(`${baseApiURL()}/library/updateLibraryBook/${payload._id}`, payload)
      : axios.post(`${baseApiURL()}/library/addLibraryBook`, payload);

    request
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          toast.success(response.data.message);
          fetchBooks();
          if (isUpdate) {
            setEditData(null);
            setSelectedTab("view");
          } else {
            setFormData(initialForm);
            setSelectedTab("view");
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error.response?.data?.message || error.message);
      });
  };

  const deleteBookHandler = (id) => {
    toast.loading("Deleting book...");
    axios
      .delete(`${baseApiURL()}/library/deleteLibraryBook/${id}`)
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          toast.success(response.data.message);
          fetchBooks();
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error.response?.data?.message || error.message);
      });
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Please upload a valid Excel file (.xlsx)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "library-books-excel");

    setImporting(true);
    setImportSummary(null);

    axios
      .post(`${baseApiURL()}/library/bulkUpload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setImporting(false);
        if (response.data.success) {
          toast.success("Books imported successfully");
          setImportSummary(response.data.summary);
          fetchBooks();
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        setImporting(false);
        toast.error(error.response?.data?.message || error.message);
      });
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        "Book Name": "Blockchain Basics",
        "Book Code": 1001,
        Author: "Mary Stone",
        Genre: "CSE",
        Quantity: 5,
        Rack: "R-12",
        Publisher: "TechPress",
        "Published Year": 2023,
        Notes: "Reference copy",
      },
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Books");
    XLSX.writeFile(workbook, "library_books_template.xlsx");
    toast.success("Template downloaded");
  };

  const renderForm = (data, setData, title, onSubmit, onCancel) => (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center space-x-2 mb-4">
        <FiBook className="text-indigo-600 dark:text-indigo-400 text-lg" />
        <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">{title}</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Book Name *
          </label>
          <input
            type="text"
            value={data.bookName}
            onChange={(e) => setData({ ...data, bookName: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter book name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Book Code *
          </label>
          <input
            type="number"
            value={data.bookCode}
            onChange={(e) => setData({ ...data, bookCode: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter book code"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Author *
          </label>
          <input
            type="text"
            value={data.author}
            onChange={(e) => setData({ ...data, author: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter author name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Genre
          </label>
          <input
            type="text"
            value={data.genre}
            onChange={(e) => setData({ ...data, genre: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g. ECE, MECH, Fiction"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Quantity *
          </label>
          <input
            type="number"
            min="1"
            value={data.quantity}
            onChange={(e) => setData({ ...data, quantity: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter available copies"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Rack / Location
          </label>
          <input
            type="text"
            value={data.rackNumber}
            onChange={(e) => setData({ ...data, rackNumber: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g. Rack 3 - Shelf B"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Publisher
          </label>
          <input
            type="text"
            value={data.publisher}
            onChange={(e) => setData({ ...data, publisher: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Publisher name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Published Year
          </label>
          <input
            type="number"
            value={data.publishedYear}
            onChange={(e) => setData({ ...data, publishedYear: e.target.value })}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g. 2024"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Notes
          </label>
          <textarea
            value={data.notes}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            rows={3}
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            placeholder="Add any remarks (optional)"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200 dark:border-slate-800">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer"
        >
          Save
        </button>
      </div>
    </div>
  );

  const renderImportCard = () => (
    <div className="mt-8 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
            <FiUploadCloud className="text-indigo-600 dark:text-indigo-400" /> Import from Excel
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Bulk upload or update books using the provided template.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadTemplate}
            className="px-4 py-2 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-white dark:hover:bg-slate-900 transition cursor-pointer"
          >
            Download Template
          </button>
          <label className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl cursor-pointer shadow-xs transition">
            {importing ? "Uploading..." : "Upload Excel"}
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              onChange={handleImport}
              className="hidden"
              disabled={importing}
            />
          </label>
        </div>
      </div>
      {importSummary && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Inserted</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{importSummary.inserted}</p>
          </div>
          <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Updated</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{importSummary.updated}</p>
          </div>
          <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Processed</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {importSummary.totalProcessed}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderViewTable = () => (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, author, or code..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">{filteredBooks.length} books found</p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs text-left">
          <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Author</th>
              <th className="px-4 py-3">Genre</th>
              <th className="px-4 py-3">Rack</th>
              <th className="px-4 py-3">Available</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
            {filteredBooks.map((item) => {
              const available = (item.quantity || 0) - (item.issuedCount || 0);
              return (
                <tr key={item._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                  <td className="px-4 py-3 font-mono font-bold">{item.bookCode}</td>
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-900 dark:text-white">{item.bookName}</p>
                    <p className="text-[11px] text-slate-400">{item.publisher || "Publisher N/A"}</p>
                  </td>
                  <td className="px-4 py-3">{item.author}</td>
                  <td className="px-4 py-3">{item.genre || "-"}</td>
                  <td className="px-4 py-3 font-mono">{item.rackNumber || "-"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border ${
                        available > 0
                          ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                          : "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800"
                      }`}
                    >
                      {available} / {item.quantity}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 transition p-1 cursor-pointer"
                        onClick={() => {
                          setEditData(item);
                          setSelectedTab("edit");
                        }}
                        title="Edit"
                      >
                        <MdEdit size={16} />
                      </button>
                      <button
                        className="text-rose-600 dark:text-rose-400 hover:text-rose-800 transition p-1 cursor-pointer"
                        onClick={() => deleteBookHandler(item._id)}
                        title="Delete"
                      >
                        <MdOutlineDelete size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredBooks.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center py-12 text-slate-400 text-xs">
                  No books match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Titles</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{books.length}</p>
        </div>
        <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Copies</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.totalCopies}</p>
        </div>
        <div className="p-4 bg-blue-50/60 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-xl">
          <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Currently Issued</p>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">{stats.totalIssued}</p>
        </div>
        <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-800/60 rounded-xl">
          <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Available</p>
          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">{stats.available}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
          <h3 className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
            <FiBarChart2 className="text-indigo-600 dark:text-indigo-400" /> Top Borrowed Titles
          </h3>
          {stats.topBorrowed.length === 0 ? (
            <p className="text-xs text-slate-400">No data yet.</p>
          ) : (
            <ul className="space-y-2">
              {stats.topBorrowed.map((book, idx) => (
                <li
                  key={book._id}
                  className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 rounded-xl text-xs"
                >
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {idx + 1}. {book.bookName}
                    </p>
                    <p className="text-[11px] text-slate-400">{book.author}</p>
                  </div>
                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                    {book.issuedCount || 0} issued
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
          <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
            Low Stock Alerts
          </h3>
          {stats.lowStock.length === 0 ? (
            <p className="text-xs text-slate-400">All titles have sufficient stock.</p>
          ) : (
            <ul className="space-y-2">
              {stats.lowStock.map((book) => {
                const available = (book.quantity || 0) - (book.issuedCount || 0);
                return (
                  <li
                    key={book._id}
                    className="flex items-center justify-between p-3 border border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-950/40 rounded-xl text-xs"
                  >
                    <div>
                      <p className="font-bold text-rose-800 dark:text-rose-300">{book.bookName}</p>
                      <p className="text-[11px] text-rose-600 dark:text-rose-400">
                        Available {available} / {book.quantity}
                      </p>
                    </div>
                    <span className="text-[10px] font-bold text-rose-700 dark:text-rose-300 uppercase tracking-wide">
                      Restock Soon
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
        <div className="bento-header-banner flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
              <FiBook className="text-lg" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Library Catalogue</h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                Add, import, and keep track of every title with ease.
              </p>
            </div>
          </div>
          <div className="flex space-x-1.5 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            {["add", "view", "analytics"].map((tab) => (
              <button
                key={tab}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold capitalize transition cursor-pointer ${
                  selectedTab === tab
                    ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
                onClick={() => {
                  setSelectedTab(tab);
                  if (tab === "add") {
                    setFormData(initialForm);
                  }
                }}
              >
                {tab === "add" ? "Add Book" : tab}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {selectedTab === "add" && (
            <>
              {renderForm(
                formData,
                setFormData,
                "Book Information",
                () => handleSubmit(formData),
                () => setFormData(initialForm)
              )}
              {renderImportCard()}
            </>
          )}

          {selectedTab === "view" && renderViewTable()}

          {selectedTab === "analytics" && renderAnalytics()}

          {selectedTab === "edit" && editData && (
            <div className="space-y-6">
              {renderForm(
                editData,
                setEditData,
                "Update Book Details",
                () => handleSubmit(editData, true),
                () => {
                  setEditData(null);
                  setSelectedTab("view");
                }
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddBook;
